import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TRAINING_STEPS, TrainingStep } from './trainingSteps';
import { getWalkthroughProgress, saveWalkthroughProgress } from '../services/trainingAssessment';

interface TrainingModeContextType {
  isActive: boolean;
  projectId: string | null;
  steps: TrainingStep[];
  currentStepIndex: number;
  currentStep: TrainingStep | null;
  startTraining: (projectId: string) => void;
  stopTraining: () => void;
  goToStep: (index: number) => void;
  nextStep: () => void;
  prevStep: () => void;
}

const TrainingModeContext = createContext<TrainingModeContextType | null>(null);

export function TrainingModeProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const navigateToStep = useCallback((index: number, pid: string | null) => {
    const step = TRAINING_STEPS[index];
    if (!step || !pid) return;
    const url = step.tab ? `/projects/${pid}?tab=${step.tab}` : `/projects/${pid}`;
    navigate(url);
  }, [navigate]);

  // Resume where the trainee left off. Progress is stored server-side, so a
  // reload no longer sends them back to step one.
  const startTraining = useCallback(async (pid: string) => {
    setProjectId(pid);
    setIsActive(true);

    let resumeIndex = 0;
    try {
      const progress = await getWalkthroughProgress(pid);
      const saved = progress?.walkthroughStepIndex ?? 0;
      if (saved > 0 && saved < TRAINING_STEPS.length) resumeIndex = saved;
    } catch {
      // Start from the beginning if progress cannot be read. Never block training.
    }

    setCurrentStepIndex(resumeIndex);
    navigateToStep(resumeIndex, pid);
  }, [navigateToStep]);

  const stopTraining = useCallback(() => {
    setIsActive(false);
  }, []);

  const goToStep = useCallback((index: number) => {
    if (index < 0 || index >= TRAINING_STEPS.length) return;
    setCurrentStepIndex(index);
    navigateToStep(index, projectId);
    if (projectId) {
      saveWalkthroughProgress(projectId, index, index === TRAINING_STEPS.length - 1)
        .catch(() => { /* progress saving must never interrupt the walkthrough */ });
    }
  }, [projectId, navigateToStep]);

  const nextStep = useCallback(() => goToStep(currentStepIndex + 1), [currentStepIndex, goToStep]);
  const prevStep = useCallback(() => goToStep(currentStepIndex - 1), [currentStepIndex, goToStep]);

  const currentStep = isActive ? (TRAINING_STEPS[currentStepIndex] || null) : null;

  const ctxValue = useMemo(
    () => ({
      isActive,
      projectId,
      steps: TRAINING_STEPS,
      currentStepIndex,
      currentStep,
      startTraining,
      stopTraining,
      goToStep,
      nextStep,
      prevStep,
    }),
    [isActive, projectId, currentStepIndex, currentStep, startTraining, stopTraining, goToStep, nextStep, prevStep]
  );

  return (
    <TrainingModeContext.Provider value={ctxValue}>
      {children}
    </TrainingModeContext.Provider>
  );
}

export function useTrainingMode() {
  const context = useContext(TrainingModeContext);
  if (!context) {
    throw new Error('useTrainingMode must be used within a TrainingModeProvider');
  }
  return context;
}
